import React, { useEffect, useMemo, useState } from 'react';
import { resolveDataHealth } from '../lib/dataHealthCopy';
import { Activity, AlertTriangle, BarChart3, RefreshCw, ShieldAlert, Zap } from 'lucide-react';
import { cn } from '../lib/utils';

import { apiV1Base } from '../lib/apiConfig';
// 2026-06-07 m-canonical-payload — formatted 5-section mover board.
import MarketPulseDecisionBoard from './MarketPulseDecisionBoard';
import {
  STATUS_COLORS,
  STATUS_LABELS,
  summarizeStatus,
  compactReason,
  formatLastUpdated,
} from '../lib/marketPulseFormatters';
type MarketPulseStatus = 'LIVE' | 'CACHED' | 'STALE' | 'PARTIAL' | 'DEGRADED' | 'UNAVAILABLE';
type AnyMap = Record<string, any>;

type Resource<T> = {
  data: T | null;
  error: string | null;
  loading: boolean;
};

const emptyResource = <T,>(): Resource<T> => ({ data: null, error: null, loading: true });

function apiBase() {
  const host = window.location.hostname;
  return apiV1Base();
}

async function marketPulseGet<T>(path: string): Promise<{ data: T | null; error: string | null }> {
  try {
    const res = await fetch(`${apiBase()}${path}`, { cache: 'no-store' });
    const payload = await res.json().catch(() => ({}));
    if (!res.ok) return { data: null, error: `HTTP ${res.status}` };
    if (payload && typeof payload === 'object' && 'ok' in payload && 'data' in payload) {
      return payload.ok ? { data: payload.data as T, error: null } : { data: null, error: payload.error?.message || 'Unavailable' };
    }
    return { data: payload as T, error: null };
  } catch (error: any) {
    return { data: null, error: error?.message || 'Network unavailable' };
  }
}

function normalizeStatus(value: unknown, fallback: MarketPulseStatus = 'UNAVAILABLE'): MarketPulseStatus {
  const text = String(value || '').toUpperCase();
  // New registry statuses map to existing internal buckets so the badge
  // class stays consistent; the label is overridden by statusDisplayLabel.
  if (text === 'FALLBACK_ACTIVE') return 'PARTIAL';
  if (text === 'CACHE_BY_QUOTA_POLICY') return 'CACHED';
  if (text === 'DELAYED_SOURCE') return 'STALE';
  if (text === 'CREDENTIAL_MISSING') return 'DEGRADED';
  if (text === 'OPTIONS_PROVIDER_NOT_CONFIGURED') return 'PARTIAL';
  if (text === 'NOT_APPLICABLE') return 'LIVE';
  if (text === 'LIMITED') return 'PARTIAL';
  if (text === 'QUOTA_LIMITED') return 'CACHED';
  if (text === 'PROVIDER_ERROR') return 'DEGRADED';
  if (text === 'UNSUPPORTED') return 'PARTIAL';
  if (text === 'PARTIAL' || text === 'PARTIAL_DATA' || text.includes('PARTIAL')) return 'PARTIAL';
  if (text.includes('LIVE') || text === 'OK' || text === 'PASS' || text === 'HEALTHY') return 'LIVE';
  if (text.includes('CACHE')) return 'CACHED';
  if (text.includes('STALE')) return 'STALE';
  if (text.includes('DEGRAD') || text.includes('WARN')) return 'DEGRADED';
  if (text.includes('ERROR') || text.includes('FAIL') || text.includes('DOWN') || text.includes('UNAVAILABLE')) return 'UNAVAILABLE';
  return fallback;
}

// [data-health-frontend-v1]
function statusDisplayLabel(status: MarketPulseStatus, rawStatus?: unknown): string {
  // Prefer the rich registry copy when we can see the raw backend status.
  if (rawStatus !== undefined && rawStatus !== null && rawStatus !== '') {
    return resolveDataHealth(rawStatus).label;
  }
  if (status === 'PARTIAL') return resolveDataHealth('LIMITED').label;
  if (status === 'LIVE') return 'Live';
  if (status === 'CACHED') return resolveDataHealth('CACHE_BY_QUOTA_POLICY').label;
  if (status === 'STALE') return 'Stale';
  if (status === 'DEGRADED') return 'Limited';
  return 'Unavailable';
}

function statusClass(status: MarketPulseStatus) {
  if (status === 'LIVE') return 'text-emerald-600 border-emerald-500/30 bg-emerald-500/10';
  if (status === 'CACHED') return 'text-sky-500 border-sky-500/30 bg-sky-500/10';
  if (status === 'PARTIAL') return 'text-yellow-500 border-yellow-500/30 bg-yellow-500/10';
  if (status === 'STALE' || status === 'DEGRADED') return 'text-amber-500 border-amber-500/30 bg-amber-500/10';
  return 'text-rose-500 border-rose-500/30 bg-rose-500/10';
}

function StatusBadge({ status, label }: { status: MarketPulseStatus; label?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-[9px] font-black uppercase tracking-widest', statusClass(status))}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label || statusDisplayLabel(status)}
    </span>
  );
}

function display(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  if (typeof value === 'number') return Number.isInteger(value) ? String(value) : value.toFixed(2);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.length ? value.map(display).join(', ') : 'None reported';
  if (typeof value === 'object') {
    return Object.entries(value as AnyMap)
      .filter(([key]) => !/secret|token|api[_-]?key|credential|password/i.test(key))
      .map(([key, val]) => `${key.replaceAll('_', ' ')}: ${display(val)}`)
      .join('; ') || 'Unavailable';
  }
  return String(value).replaceAll('_', ' ');
}

function symbolOf(item: AnyMap): string {
  return String(item.symbol || item.ticker || item.asset || '').trim().toUpperCase();
}

function isRiskOnlySymbol(symbol: string) {
  return ['VIX', '^VIX', '$VIX'].includes(symbol.trim().toUpperCase());
}

function isTradable(item: AnyMap): boolean {
  return item.tradable !== false && item.is_tradable !== false && item.tradeable !== false && item.trading_enabled !== false;
}

function firstArray(payload: unknown, keys: string[]): AnyMap[] {
  if (Array.isArray(payload)) return payload.filter((item) => item && typeof item === 'object') as AnyMap[];
  if (!payload || typeof payload !== 'object') return [];
  const obj = payload as AnyMap;
  for (const key of keys) {
    if (Array.isArray(obj[key])) return obj[key].filter((item: unknown) => item && typeof item === 'object') as AnyMap[];
  }
  if (obj.data) return firstArray(obj.data, keys);
  return [];
}

function normalizeTicker(payload: unknown) {
  return firstArray(payload, ['ticker', 'items', 'assets', 'symbols'])
    .filter(isTradable)
    .map((item) => ({
      symbol: symbolOf(item),
      name: item.display_name || item.name || item.label,
      assetClass: item.asset_class || item.assetClass || item.type || 'tradable',
      price: typeof item.price === 'number' ? item.price : Number(item.price ?? item.last_price ?? NaN),
      changePct: typeof item.change_percent === 'number' ? item.change_percent : Number(item.change_pct ?? item.changePercent ?? NaN),
      status: normalizeStatus(item.freshness_status || item.status || item.freshness, item.price == null ? 'UNAVAILABLE' : 'LIVE'),
      freshness: item.freshness_status || item.freshness || item.timestamp || item.generated_at,
      provider: item.provider || item.source,
    }))
    .filter((item) => item.symbol && !isRiskOnlySymbol(item.symbol));
}

function normalizeMovers(payload: unknown) {
  const source = payload && typeof payload === 'object' ? payload as AnyMap : {};
  const groups = Object.entries(source).flatMap(([group, rows]) => (
    Array.isArray(rows)
      ? rows.map((row) => ({ ...row, group }))
      : []
  ));
  return groups
    .filter(isTradable)
    .map((item) => ({
      symbol: symbolOf(item),
      group: String(item.group || item.asset_class || 'Movers').replaceAll('_', ' '),
      assetClass: item.asset_class || item.assetClass,
      price: item.price,
      changePct: item.change_percent ?? item.change_pct ?? item.changePercent,
      reason: item.primary_catalyst || item.reason || item.strategy_impact || item.reason_code,
      freshness: item.freshness_status || item.freshness,
    }))
    .filter((item) => item.symbol && !isRiskOnlySymbol(item.symbol))
    .slice(0, 12);
}

function freshnessFromPulse(pulse: AnyMap | null, ticker: AnyMap | null, errors: Array<string | null>, health: AnyMap | null = null) {
  if (errors.some(Boolean)) return 'UNAVAILABLE' as MarketPulseStatus;
  // Prefer the canonical overall_status from the backend (live | partial | degraded | unavailable)
  const overall =
    pulse?.overall_status ||
    health?.overall ||
    health?.overall_label;
  if (overall) return normalizeStatus(overall, 'UNAVAILABLE');
  return normalizeStatus(pulse?.freshness_status || ticker?.freshness_status || pulse?.status || ticker?.status, 'UNAVAILABLE');
}

function useMarketPulseSnapshot() {
  const [pulse, setPulse] = useState<Resource<AnyMap>>(emptyResource);
  const [ticker, setTicker] = useState<Resource<AnyMap>>(emptyResource);
  const [movers, setMovers] = useState<Resource<AnyMap>>(emptyResource);
  const [strategy, setStrategy] = useState<Resource<AnyMap>>(emptyResource);
  const [health, setHealth] = useState<Resource<AnyMap>>(emptyResource);

  async function load() {
    setPulse(emptyResource());
    setTicker(emptyResource());
    setMovers(emptyResource());
    setStrategy(emptyResource());
    setHealth(emptyResource());
    const [pulseRes, tickerRes, moversRes, strategyRes, healthRes] = await Promise.all([
      marketPulseGet<AnyMap>('/market-pulse'),
      marketPulseGet<AnyMap>('/market-pulse/ticker'),
      marketPulseGet<AnyMap>('/market-pulse/movers'),
      marketPulseGet<AnyMap>('/market-pulse/strategy-context'),
      marketPulseGet<AnyMap>('/market-pulse/health'),
    ]);
    setPulse({ data: pulseRes.data, error: pulseRes.error, loading: false });
    setTicker({ data: tickerRes.data, error: tickerRes.error, loading: false });
    setMovers({ data: moversRes.data, error: moversRes.error, loading: false });
    setStrategy({ data: strategyRes.data, error: strategyRes.error, loading: false });
    setHealth({ data: healthRes.data, error: healthRes.error, loading: false });
  }

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30000);
    return () => window.clearInterval(timer);
  }, []);

  const errors = [pulse.error, ticker.error, movers.error, strategy.error, health.error];
  return {
    pulse,
    ticker,
    movers,
    strategy,
    health,
    errors,
    reload: load,
    status: freshnessFromPulse(pulse.data, ticker.data, errors, health.data),
    tickerItems: normalizeTicker(ticker.data || pulse.data),
    moverItems: normalizeMovers(movers.data || pulse.data?.top_tradable_movers),
  };
}

function fmtPrice(value: unknown, assetClass?: string) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Unavailable';
  if (assetClass === 'forex') return num.toFixed(5);
  return `$${num.toLocaleString(undefined, { maximumFractionDigits: num >= 100 ? 2 : 4 })}`;
}

function fmtPct(value: unknown) {
  const num = Number(value);
  if (!Number.isFinite(num)) return 'Unavailable';
  return `${num > 0 ? '+' : ''}${num.toFixed(2)}%`;
}

export function MarketPulseTickerStrip({ compact = false }: { compact?: boolean }) {
  const snapshot = useMarketPulseSnapshot();
  const items = snapshot.tickerItems;

  return (
    <section data-testid="ticker-strip" data-status={snapshot.ticker.loading ? 'loading' : snapshot.status} className={cn('border-b border-[var(--card-border)] bg-[var(--card-bg)]/95 backdrop-blur', compact ? '' : 'rounded-2xl border')}>
      <div className="flex items-center gap-3 overflow-x-auto px-4 py-2">
        <div className="sticky left-0 z-10 flex shrink-0 items-center gap-2 bg-[var(--card-bg)] pr-3">
          <span className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Market Pulse</span>
          <StatusBadge status={snapshot.ticker.loading ? 'CACHED' : snapshot.status} />
        </div>
        {snapshot.ticker.loading ? (
          <div className="flex items-center gap-2 text-xs font-bold text-[var(--muted-text)]">
            <Activity size={14} className="animate-pulse" /> Loading tradable ticker feed...
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center gap-2 text-xs font-bold text-[var(--muted-text)]">
            <StatusBadge status="UNAVAILABLE" />
            {snapshot.ticker.error || 'No backend-provided tradable symbols returned.'}
          </div>
        ) : (
          items.map((item) => (
            <button
              key={item.symbol}
              type="button"
              data-testid="ticker-row"
              data-symbol={item.symbol}
              data-quote-freshness={String(item.freshness || 'unknown').toLowerCase()}
              data-quote-source={String(item.provider || 'unknown').toLowerCase()}
              onClick={() => window.dispatchEvent(new CustomEvent('adversyn:symbol-selected', { detail: { type: 'stock', value: item.symbol, label: item.name || item.symbol, asset_class: item.assetClass } }))}
              className="flex min-w-[164px] shrink-0 items-center justify-between gap-3 rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-left transition-all hover:border-emerald-500/50"
              title={display(item.freshness || item.provider)}
            >
              <span>
                <span className="block text-xs font-black text-[var(--app-text)]">{item.symbol}</span>
                <span className="block truncate text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{item.assetClass}</span>
              </span>
              <span className="text-right">
                <span className="block text-xs font-black tabular-nums text-[var(--app-text)]">{fmtPrice(item.price, item.assetClass)}</span>
                <span className={cn('block text-[10px] font-black tabular-nums', Number(item.changePct) >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{fmtPct(item.changePct)}</span>
              </span>
            </button>
          ))
        )}
      </div>
    </section>
  );
}

function Panel({ title, icon: Icon, children }: { title: string; icon: React.ElementType; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-[var(--card-border)] bg-[var(--card-bg)] p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <Icon size={16} className="text-emerald-600" />
        <h2 className="text-xs font-black uppercase tracking-widest text-[var(--app-text)]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export function MarketPulseDashboardContextCard() {
  const snapshot = useMarketPulseSnapshot();
  // Use the canonical overall_status so this banner shows the same label
  // ("Live" / "Partial Data" / "Degraded") as the Market Pulse page.
  const regimeLabel = statusDisplayLabel(snapshot.status);
  return (
    <div className="rounded-2xl border border-emerald-500/20 bg-emerald-500/5 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Market Pulse Context</div>
          <div className="mt-1 text-sm font-bold text-[var(--app-text)]">
            Regime: {regimeLabel}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={snapshot.status} label={`Freshness ${regimeLabel}`} />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
            Additive only. Existing risk and execution rules remain authoritative.
          </span>
        </div>
      </div>
    </div>
  );
}

export default function MarketPulseModule() {
  const snapshot = useMarketPulseSnapshot();
  const pulse = snapshot.pulse.data;
  const strategyContext = snapshot.strategy.data?.market_intelligence_context || snapshot.strategy.data;
  const providerHealth = snapshot.health.data?.provider_health || pulse?.provider_health || {};
  const events = [
    ...(Array.isArray(pulse?.news_catalysts) ? pulse.news_catalysts : []),
    ...(Array.isArray(pulse?.economic_events) ? pulse.economic_events : []),
    ...(Array.isArray(pulse?.earnings_events) ? pulse.earnings_events : []),
  ].filter((event: AnyMap) => {
    const symbols = event.affected_tradable_symbols || event.affectedSymbols || event.symbols || [];
    return Array.isArray(symbols) && symbols.length > 0;
  }).slice(0, 8);
  const alerts = [
    ...(Array.isArray(pulse?.volatility_alerts) ? pulse.volatility_alerts : []),
    ...(Array.isArray(pulse?.spread_alerts) ? pulse.spread_alerts : []),
    ...(Array.isArray(pulse?.liquidity_alerts) ? pulse.liquidity_alerts : []),
  ];

  return (
    <div className="app-container space-y-8 pb-12">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-4xl font-black uppercase italic tracking-tight text-[var(--app-text)] font-display">Market Pulse</h1>
          <p className="mt-1 text-sm font-medium italic text-[var(--muted-text)]">Live tradable market intelligence for Adversyn trading decisions.</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={snapshot.pulse.loading ? 'CACHED' : snapshot.status} />
          <button onClick={snapshot.reload} className="flex items-center gap-2 rounded-xl border border-[var(--card-border)] bg-[var(--card-bg)] px-3 py-2 text-[10px] font-black uppercase text-[var(--muted-text)] hover:text-emerald-600">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </div>

      {/* 2026-06-07 m-canonical-payload — formatted decision-board view */}
      <MarketPulseDecisionBoard />

      <MarketPulseTickerStrip />

      {snapshot.errors.some(Boolean) && (
        <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm font-bold text-amber-500">
          Market Pulse is degraded: {snapshot.errors.filter(Boolean).join('; ')}
        </div>
      )}

      <div className="grid gap-6 xl:grid-cols-4">
        <Panel title="Market Regime Summary" icon={BarChart3}>
          <div className="space-y-3 text-sm">
            <Field label="Regime" value={pulse?.market_regime?.label || pulse?.market_regime} />
            <Field label="Risk Direction" value={pulse?.market_regime?.risk_direction} />
            <Field label="Freshness" value={pulse?.freshness_status || snapshot.status} />
            <Field label="Generated" value={pulse?.generated_at} />
          </div>
        </Panel>

        <Panel title="Top Tradable Movers" icon={Zap}>
          {snapshot.movers.loading ? (
            <div className="text-sm font-bold text-[var(--muted-text)]">Loading movers...</div>
          ) : snapshot.moverItems.length === 0 ? (
            <div className="text-sm font-bold text-[var(--muted-text)]">No backend-provided tradable movers are available.</div>
          ) : (
            <div className="space-y-2">
              {snapshot.moverItems.slice(0, 7).map((mover) => (
                <div key={`${mover.group}-${mover.symbol}`} className="rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-black text-[var(--app-text)]">{mover.symbol}</div>
                      <div className="text-[9px] font-bold uppercase tracking-widest text-[var(--muted-text)]">{mover.group}</div>
                    </div>
                    <span className={cn('text-xs font-black', Number(mover.changePct) >= 0 ? 'text-emerald-600' : 'text-rose-600')}>{fmtPct(mover.changePct)}</span>
                  </div>
                  <div className="mt-2 text-[10px] font-bold text-[var(--muted-text)]">{display(mover.reason || mover.freshness)}</div>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Catalysts / Events" icon={Activity}>
          <CatalystsPanelContent
            events={events}
            newsPayload={pulse?.news_payload}
            calendarPayload={pulse?.calendar_payload}
          />
        </Panel>

        <Panel title="Alerts" icon={AlertTriangle}>
          {alerts.length === 0 ? (
            <div className="text-sm font-bold text-[var(--muted-text)]">No volatility, spread, liquidity, or provider warnings returned.</div>
          ) : (
            <div className="space-y-2">
              {alerts.slice(0, 8).map((alert: AnyMap, index) => (
                <div key={index} className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-3 text-xs font-bold text-amber-500">
                  {isRiskOnlySymbol(symbolOf(alert)) ? 'VIX risk context: ' : ''}{display(alert)}
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <Panel title="Strategy Context Summary" icon={ShieldAlert}>
          <p className="text-sm font-medium leading-relaxed text-[var(--app-text)]">{display(strategyContext)}</p>
        </Panel>
        <Panel title="Provider / Freshness Status" icon={Activity}>
          <div className="space-y-2">
            {Object.entries(providerHealth).length === 0 ? (
              <div className="text-sm font-bold text-[var(--muted-text)]">No provider status returned.</div>
            ) : Object.entries(providerHealth).map(([provider, value]) => (
              <div key={provider} className="flex items-start justify-between gap-4 rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3 text-sm">
                <span className="font-black uppercase text-[var(--app-text)]">{provider.replaceAll('_', ' ')}</span>
                <span className="max-w-[70%] text-right text-[var(--muted-text)]">{display(value)}</span>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value: unknown }) {
  return (
    <div className="rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3">
      <div className="text-[9px] font-black uppercase tracking-widest text-[var(--muted-text)]">{label}</div>
      <div className="mt-1 text-sm font-black text-[var(--app-text)]">{display(value)}</div>
    </div>
  );
}

// Honest status badge driven by the canonical Market News Status contract.
function MarketNewsStatusPill({ status }: { status: unknown }) {
  const level = summarizeStatus(status);
  const label = STATUS_LABELS[level];
  const color = STATUS_COLORS[level];
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-widest"
      style={{ borderColor: color, color }}
      data-testid="catalyst-status-badge"
      data-status={String(status ?? '').toUpperCase()}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }} />
      {label}
    </span>
  );
}

// Catalysts / Events panel content — uses canonical news_payload / calendar_payload
// shape so the user always sees status + reason instead of a bare "no catalysts" line.
function CatalystsPanelContent({
  events,
  newsPayload,
  calendarPayload,
}: {
  events: AnyMap[];
  newsPayload?: AnyMap | null;
  calendarPayload?: AnyMap | null;
}) {
  const newsStatus = String(newsPayload?.status || newsPayload?.freshness_status || 'UNAVAILABLE').toUpperCase();
  const calStatus = String(calendarPayload?.status || calendarPayload?.freshness_status || 'UNAVAILABLE').toUpperCase();

  // Worst-of for the panel-level badge.
  const rank: Record<string, number> = { LIVE: 0, LIMITED: 1, STALE: 2, ERROR: 3, UNAVAILABLE: 4 };
  const worst = rank[calStatus] >= rank[newsStatus] ? calStatus : newsStatus;

  const newsSource = newsPayload?.source || newsPayload?.provider || null;
  const calSource = calendarPayload?.source || calendarPayload?.provider || null;
  const newsUpdated = newsPayload?.last_updated || null;
  const calUpdated = calendarPayload?.last_updated || null;

  return (
    <div className="space-y-3" data-testid="catalysts-panel">
      <div className="flex items-center justify-between gap-3">
        <MarketNewsStatusPill status={worst} />
        <div className="text-right text-[9px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
          <div>News: {newsSource ? String(newsSource) : 'Not connected'}</div>
          <div>Calendar: {calSource ? String(calSource) : 'Not connected'}</div>
          {(newsUpdated || calUpdated) && (
            <div>Updated {formatLastUpdated(newsUpdated || calUpdated) || '—'}</div>
          )}
        </div>
      </div>

      {events.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-[var(--input-border)] bg-[var(--input-bg)] p-3"
          data-testid="catalysts-empty"
        >
          <div className="text-sm font-bold text-[var(--app-text)]">
            {newsPayload?.display_message
              || newsPayload?.message
              || (worst === 'LIVE'
                  ? 'No tradable-symbol catalysts at this time.'
                  : 'No catalysts available — no verified provider connected.')}
          </div>
          <div className="mt-1 text-[10px] font-bold text-[var(--muted-text)] uppercase tracking-widest">
            Reason: {compactReason(newsPayload?.reason_code || calendarPayload?.reason_code || 'PROVIDER_UNAVAILABLE')}
          </div>
          <div className="mt-1 text-[10px] italic text-[var(--muted-text)]">
            Headlines and catalysts are never fabricated.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {events.map((event: AnyMap, index) => {
            const itemStatus = String(event.freshness_status || event.status || worst).toUpperCase();
            return (
              <div key={event.dedupe_id || event.id || index} className="rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] p-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-sm font-black text-[var(--app-text)]">{display(event.headline || event.title)}</div>
                  <MarketNewsStatusPill status={itemStatus} />
                </div>
                <div className="mt-2 text-[10px] font-bold uppercase tracking-widest text-[var(--muted-text)]">
                  {display(event.affected_tradable_symbols || event.affectedSymbols)}
                  {event.source ? ` | ${display(event.source)}` : ''}
                  {event.last_updated ? ` | ${formatLastUpdated(event.last_updated)}` : ''}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
